import{Component}from '@theme/component';import{debounce,onDocumentLoaded,setHeaderMenuStyle}from '@theme/utilities';import{MegaMenuHoverEvent}from '@theme/events';

class HeaderMenu extends Component{
  requiredRefs=['overflowMenu'];
  #submenuMutationObserver=null;
  #deactivateTimeout=null;
  #switchTimeout=null;
  #lastMousePos={x:0,y:0};
  #prevMousePos={x:0,y:0};

  connectedCallback(){
    super.connectedCallback();
    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize',this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave',this.#overflowSubmenuListener);
    document.addEventListener('pointermove',this.#trackMouse,{passive:true});
  }

  disconnectedCallback(){
    super.disconnectedCallback();
    window.removeEventListener('resize',this.#resizeListener);
    this.overflowMenu?.removeEventListener('pointerleave',this.#overflowSubmenuListener);
    document.removeEventListener('pointermove',this.#trackMouse);
    this.#cleanupMutationObserver();
    this.#clearAllTimeouts();
  }

  #clearAllTimeouts(){
    if(this.#deactivateTimeout){clearTimeout(this.#deactivateTimeout);this.#deactivateTimeout=null}
    if(this.#switchTimeout){clearTimeout(this.#switchTimeout);this.#switchTimeout=null}
  }

  #trackMouse=(e)=>{
    this.#prevMousePos={...this.#lastMousePos};
    this.#lastMousePos={x:e.clientX,y:e.clientY};
  };

  #resizeListener=debounce(()=>{setHeaderMenuStyle()},100);
  #overflowSubmenuListener=()=>{this.#doDeactivate()};

  #state={activeItem:null,};

  get overflowMenu(){return(this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'))}
  get overflowListHovered(){return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover')??!1}
  get headerComponent(){return(this.closest('header-component'))}

  /* Check if mouse is moving toward the open submenu (diagonal aim) */
  #isAimingAtSubmenu(activeItem){
    const submenu=findSubmenu(activeItem);
    if(!submenu)return!1;
    const headerComp=this.headerComponent;
    if(!headerComp)return!1;

    /* Get the submenu visible area - approximate using header bottom */
    const headerRect=headerComp.getBoundingClientRect();
    const submenuTop=headerRect.bottom;
    const mouseY=this.#lastMousePos.y;
    const prevMouseY=this.#prevMousePos.y;
    const dy=mouseY-prevMouseY;

    /* If the mouse is in the header area and moving downward, user is likely aiming at submenu */
    if(mouseY<=submenuTop+15&&dy>0){
      return!0;
    }
    /* Also check: if mouse is above submenu and has significant downward velocity relative to horizontal */
    if(mouseY<submenuTop+30){
      const dx=Math.abs(this.#lastMousePos.x-this.#prevMousePos.x);
      if(dy>0&&dy>=dx*0.3){
        return!0;
      }
    }
    return!1;
  }

  activate=(event)=>{
    /* Clear any pending deactivation */
    if(this.#deactivateTimeout){clearTimeout(this.#deactivateTimeout);this.#deactivateTimeout=null}

    this.dispatchEvent(new MegaMenuHoverEvent());
    if(!(event.target instanceof Element)||!this.headerComponent)return;
    let item=findMenuItem(event.target);
    if(!item||item==this.#state.activeItem)return;

    /* If there's an active item with a submenu, check for diagonal aim */
    const currentActiveItem=this.#state.activeItem;
    const currentSubmenu=findSubmenu(currentActiveItem);

    if(currentActiveItem&&currentSubmenu&&this.#isAimingAtSubmenu(currentActiveItem)){
      /* User seems to be moving diagonally toward the open submenu - delay the switch */
      if(this.#switchTimeout)clearTimeout(this.#switchTimeout);

      const pendingEvent=event;
      const pendingItem=item;

      this.#switchTimeout=setTimeout(()=>{
        this.#switchTimeout=null;
        /* Re-check: is the submenu being hovered now? */
        if(currentSubmenu.matches(':hover'))return;
        const listItem=currentActiveItem.closest('.menu-list__list-item');
        if(listItem&&listItem.matches(':hover'))return;
        /* Still aiming? Give another chance */
        if(this.#isAimingAtSubmenu(currentActiveItem)){
          /* One more short delay */
          this.#switchTimeout=setTimeout(()=>{
            this.#switchTimeout=null;
            if(currentSubmenu.matches(':hover'))return;
            this.#doActivate(pendingEvent,pendingItem);
          },100);
          return;
        }
        this.#doActivate(pendingEvent,pendingItem);
      },200);
      return;
    }

    /* Clear switch timeout if switching to new item directly */
    if(this.#switchTimeout){clearTimeout(this.#switchTimeout);this.#switchTimeout=null}
    this.#doActivate(event,item);
  };

  #doActivate=(event,item)=>{
    if(!this.headerComponent)return;
    /* Re-check item is still valid */
    if(!item)return;

    const isDefaultSlot=event.target.slot==='';
    this.dataset.overflowExpanded=(!isDefaultSlot).toString();

    const previouslyActiveItem=this.#state.activeItem;
    if(previouslyActiveItem){previouslyActiveItem.ariaExpanded='false'}

    this.#state.activeItem=item;
    this.ariaExpanded='true';
    item.ariaExpanded='true';

    let submenu=findSubmenu(item);
    const hasSubmenu=Boolean(submenu);
    const isCompact=submenu?.hasAttribute('data-compact');
    if(!hasSubmenu&&!isDefaultSlot){submenu=this.overflowMenu}

    /* For compact dropdowns, skip the underlay height system entirely */
    if(isCompact){
      submenu.dataset.active='';
      this.headerComponent.style.setProperty('--submenu-height','0px');
      this.#setFullOpenHeaderHeight(0);
      this.style.setProperty('--submenu-opacity','1');
      return;
    }

    if(submenu){
      submenu.dataset.active='';
      this.#cleanupMutationObserver();
      this.#submenuMutationObserver=new MutationObserver(()=>{
        requestAnimationFrame(()=>{requestAnimationFrame(()=>{
          if(submenu.offsetHeight>0){
            this.headerComponent?.style.setProperty('--submenu-height',`${submenu.offsetHeight}px`);
            this.#cleanupMutationObserver();
          }
        })});
      });
      this.#submenuMutationObserver.observe(submenu,{childList:!0,subtree:!0});
      setTimeout(()=>{this.#cleanupMutationObserver()},500);
    }

    let finalHeight=submenu?.offsetHeight||0;
    if(!isDefaultSlot){
      const overflowListHeight=this.#getOverflowListLinksHeight();
      if(hasSubmenu){
        const overflowHeight=this.overflowMenu?.offsetHeight||0;
        finalHeight=Math.max(overflowHeight,overflowListHeight);
      }else{
        finalHeight=overflowListHeight;
      }
    }
    if(!submenu){finalHeight=0}

    this.headerComponent.style.setProperty('--submenu-height',`${finalHeight}px`);
    this.#setFullOpenHeaderHeight(finalHeight);
    this.style.setProperty('--submenu-opacity','1');
  };

  deactivate(event){
    if(!(event.target instanceof Element))return;
    const menu=findSubmenu(this.#state.activeItem);
    const isMovingWithinMenu=event.relatedTarget instanceof Node&&menu?.contains(document.activeElement);
    const isMovingToSubmenu=event.relatedTarget instanceof Node&&menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu=event.relatedTarget instanceof Node&&event.relatedTarget.parentElement?.matches('[slot="overflow"]');
    if(isMovingWithinMenu||isMovingToOverflowMenu||isMovingToSubmenu)return;

    const activeItem=this.#state.activeItem;
    if(this.#deactivateTimeout){clearTimeout(this.#deactivateTimeout);this.#deactivateTimeout=null}

    this.#deactivateTimeout=setTimeout(()=>{
      this.#deactivateTimeout=null;
      if(!activeItem||activeItem!=this.#state.activeItem)return;
      const submenu=findSubmenu(activeItem);
      const listItem=activeItem.closest('.menu-list__list-item');
      if(submenu&&submenu.matches(':hover'))return;
      if(listItem&&listItem.matches(':hover'))return;
      if(this.overflowListHovered||this.overflowMenu?.matches(':hover'))return;
      this.#doDeactivate(activeItem);
    },150);
  }

  #doDeactivate=(item=this.#state.activeItem)=>{
    if(!item||item!=this.#state.activeItem)return;
    if(this.overflowListHovered||this.overflowMenu?.matches(':hover'))return;
    this.headerComponent?.style.setProperty('--submenu-height','0px');
    this.#setFullOpenHeaderHeight(0);
    this.style.setProperty('--submenu-opacity','0');
    this.dataset.overflowExpanded='false';
    const submenu=findSubmenu(item);
    this.#state.activeItem=null;
    this.ariaExpanded='false';
    item.ariaExpanded='false';
    if(submenu){delete submenu.dataset.active}
  };

  #getOverflowListLinksHeight(){
    const slottedMenuLinks=this.overflowMenu?.querySelector('slot')?.assignedElements();
    if(!slottedMenuLinks)return this.overflowMenu?.offsetHeight||0;
    const mapSubmenus=(cb)=>{slottedMenuLinks.forEach((link)=>{const submenu=(link.querySelector('[ref="submenu[]"]'));if(submenu){cb(submenu)}})};
    mapSubmenus((submenu)=>{submenu.style.setProperty('display','none')});
    const height=this.overflowMenu?.offsetHeight||0;
    mapSubmenus((submenu)=>{submenu.style.removeProperty('display')});
    return height;
  }

  #setFullOpenHeaderHeight(submenuHeight){
    if(!this.headerComponent)return;
    const isOverlapSituation=this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');
    const headerVisibleHeight=isOverlapSituation&&this.headerComponent.offsetHeight>0?(this.headerComponent.querySelector('.header__row--top'))?.offsetHeight??0:this.headerComponent.offsetHeight;
    const nothingToOpen=submenuHeight===0;
    const fullOpenHeaderHeight=nothingToOpen?0:submenuHeight+(headerVisibleHeight??0);
    this.headerComponent?.style.setProperty('--full-open-header-height',`${fullOpenHeaderHeight}px`);
  }

  #preloadImages=()=>{
    const images=this.querySelectorAll('img[loading="lazy"]');
    images?.forEach((image)=>image.removeAttribute('loading'));
  };

  #cleanupMutationObserver(){
    this.#submenuMutationObserver?.disconnect();
    this.#submenuMutationObserver=null;
  }
}

if(!customElements.get('header-menu')){customElements.define('header-menu',HeaderMenu)}

function findMenuItem(element){
  if(!(element instanceof Element))return null;
  if(element?.matches('[slot="more"')){return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'))}
  return element?.querySelector('[ref="menuitem"]');
}

function findSubmenu(element){
  const submenu=element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement?submenu:null;
}